"use client";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { permanentRedirect, usePathname, useRouter } from "next/navigation";
import { Suspense, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useHeader } from "./hooks/data-api";
import { Search } from "./search-input";
import { Timezone } from "./timezone";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { UITheme } from "@/provider/data/type";
import shTitle from "@/public/static/shareholder-title.png";
import searchIconBlue from "@/public/static/sh-icons/search-icon-blue.gif";
import { GrTest } from "react-icons/gr";

export function ShareHolder({
  form,
  showSearch = true,
}: {
  form: UseFormReturn<any>;
  showSearch?: boolean;
}) {
  const { data, isLoading } = useHeader();
  const { data: session, status } = useSession();
  const shareHolderUrl = process.env.NEXT_PUBLIC_O_AUTH_URL;
  const [text, setText] = useState<string>("");
  const router = useRouter();
  const pathname = usePathname();

  if (status === "unauthenticated" || session?.error == "InvalidAccessToken") {
    if (session?.error == "InvalidAccessToken") {
      permanentRedirect(
        `/login?callbackUrl=${encodeURIComponent(pathname)}&error=InvalidAccessToken`,
      );
    } else {
      permanentRedirect(`/login?callbackUrl=${encodeURIComponent(pathname)}`);
    }
  }

  return (
    <Suspense>
      <div
        className={cn(
          "mt-0 pt-0",
          isLoading
            ? `bg-gradient-to-t from-sh-shareholder2016-bottom to-sh-shareholder2016-top`
            : data?.theme === "moonlight"
              ? `bg-gradient-to-t from-sh-moonlight-bottom to-sh-moonlight-top`
              : data?.theme === "highcontrast"
                ? `bg-gradient-to-t from-sh-highcontrast-bottom to-sh-highcontrast-top`
                : `bg-gradient-to-t from-sh-shareholder2016-bottom to-sh-shareholder2016-top`,
        )}
      >
        <div className="h-18 ml-0 mr-6 flex flex-col py-1">
          {/* top */}
          <div className="flex w-full flex-row justify-between">
            {/* top-left */}
            <div className="flex">
              <a href={shareHolderUrl}>
                <Image
                  src={shTitle}
                  alt={"back to ShareHolder"}
                  width={240}
                  height={30}
                  className="h-6 w-[220px]"
                  unoptimized
                  priority
                />
              </a>
            </div>
            {/* top-right */}
            <div
              className={cn(
                "hidden flex-row lg:flex",
                data?.theme === "moonlight"
                  ? " text-blue-300"
                  : data?.theme === "highcontrast"
                    ? "text-green-200"
                    : "text-blue-950",
              )}
            >
              <div className="flex flex-col">
                <div className="mr-2 flex flex-row space-x-1">
                  <Timezone></Timezone>
                  <Timezone timeZone="Europe/London" label="London:"></Timezone>
                  <Timezone timeZone="CET" label="CET:"></Timezone>
                  <Timezone timeZone="America/New_York" label="NY:"></Timezone>
                  <Timezone
                    timeZone="America/Los_Angeles"
                    label="LA:"
                  ></Timezone>
                  <GrTest
                    size={10}
                    onClick={async () => {
                      // localhost:3000/micro-structure
                      let currentBaseUrl = window.location.href;
                      // localhost:3000
                      currentBaseUrl = currentBaseUrl.split("/")[2];
                      // http://localhost:3000
                      if (currentBaseUrl.includes("localhost")) {
                        currentBaseUrl = "http://" + currentBaseUrl;
                      } else {
                        currentBaseUrl = "https://" + currentBaseUrl;
                      }
                      window.open(
                        `${currentBaseUrl}/static/html-report/index.html`,
                        "_blank",
                        "width=800,height=600",
                      );
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 128 128"
                    onClick={() => {
                      window.open(
                        "/chart-test",
                        "_blank",
                        "width=1200,height=1000",
                      );
                    }}
                  >
                    <path
                      fill="#f37036"
                      d="M113.117 26.066C92.168-1.062 53.191-6.07 26.062 14.883c-27.125 20.953-32.128 59.93-11.175 87.055c20.957 27.124 59.937 32.124 87.058 11.167c27.114-20.953 32.118-59.918 11.172-87.039m0 0"
                    />
                    <path
                      fill="#fff"
                      d="M91.078 24.164a10.04 10.04 0 0 0-5.781 2.426a10.03 10.03 0 0 0-1.54 13.465a10.03 10.03 0 0 0 13.276 2.715h.002v.001l.156.155a10.6 10.6 0 0 0 1.965-1.45A10.34 10.34 0 0 0 99 27.107v-.002l-8.844 8.789l-.156-.155l8.844-8.793a10.04 10.04 0 0 0-7.766-2.78zM79.434 38.551c-4.24-.007-11.163 4.799-28.067 21.703l.084.086c-.092-.032-.185-.035-.185-.035l-6.364 6.308a1.035 1.035 0 0 0 .93 1.762l10.914-2.328a.3.3 0 0 0 .092-.17l.242.25l-3.72 3.69h-.18l-22.086 22.26l7.086 6.824a1.25 1.25 0 0 0 1.476.149a1.33 1.33 0 0 0 .645-1.356l-1.035-4.5a.53.53 0 0 1 0-.62a117.3 117.3 0 0 0 26.738-17.583l-4.535-4.537l.086-.014l-2.69-2.689l.172-.174l.182.186l-.094.091l7.137 7.293v-.003c13.68-12.954 23.39-23.367 20.865-30.375a3.83 3.83 0 0 0-1.107-2.208v.004a4 4 0 0 0-.483-.306c-.083-.088-.156-.178-.244-.264l-.066.066a4 4 0 0 0-.582-.29l.289-.292c-1.796-1.6-3.28-2.924-5.5-2.93zM30.94 92.21l-5.171 5.172v.004a1.03 1.03 0 0 0-.457 1.125a1.035 1.035 0 0 0 .921.789l12.672.875z"
                    />
                    <path
                      fill="#f37036"
                      d="M91.95 23.31a11.05 11.05 0 0 0-7.759 3.17a10.99 10.99 0 0 0-2.39 11.641c-4.741-2.03-11.155 1.51-31.106 21.457a1 1 0 0 0-.037.094a1 1 0 0 0-.119.062l-6.309 6.364a1.97 1.97 0 0 0-.363 2.324a2.01 2.01 0 0 0 1.707.984l.313-.203l8.424-1.797l-4.03 4.067a1 1 0 0 0-.054.166l-19.75 19.799a.8.8 0 0 0-.192.238l-5.086 5.09a1.97 1.97 0 0 0-.414 2.043a2 2 0 0 0 1.656 1.265l12.618.88a1 1 0 0 0 .52-.415a.89.89 0 0 0 0-1.035l-.026-.025a2.2 2.2 0 0 0 .705-.58a2.24 2.24 0 0 0 .406-1.876l-.984-4.187a126.7 126.7 0 0 0 26.334-16.861a1 1 0 0 0 .248.103c.254-.019.492-.128.672-.308c13.55-12.83 21.515-21.622 21.515-28.602a8 8 0 0 0-.431-2.85a11 11 0 0 0 3.845.83l-.015.004a11.2 11.2 0 0 0 5.183-1.45l.004.001a.84.84 0 0 0 .617-.055a9.4 9.4 0 0 0 2.07-1.652a10.87 10.87 0 0 0 3.258-7.758a10.87 10.87 0 0 0-3.257-7.758a1 1 0 0 0-.118-.091a11.05 11.05 0 0 0-7.656-3.078zm-.087 1.772a9.27 9.27 0 0 1 5.586 1.914l-8.068 8.117a1 1 0 0 0-.076.098a.83.83 0 0 0-.239.55a.83.83 0 0 0 .313.65h.002l6.1 6.1a9.04 9.04 0 0 1-10.028-1.913c-2.586-2.6-3.336-6.504-1.953-9.891c1.383-3.39 4.68-5.605 8.363-5.625m7.12 3.432a8.87 8.87 0 0 1 2.033 5.674a9.15 9.15 0 0 1-2.688 6.464a10 10 0 0 1-1.098.895L92.307 36.7l-.963-.963l.265-.265l7.373-6.96zm-.366 4.193a.78.78 0 0 0-.55.031a.73.73 0 0 0-.36.426a.73.73 0 0 0 .05.559a2.23 2.23 0 0 1-.257 2.328a.64.64 0 0 0-.195.488c.004.184.07.36.195.492a.58.58 0 0 0 .414 0a.68.68 0 0 0 .672-.207a3.57 3.57 0 0 0 .465-3.777v.004a.78.78 0 0 0-.434-.344M79.34 39.43a5.6 5.6 0 0 1 3.31 1.226a4.76 4.76 0 0 0-2.681 1.34L57.162 64.701l-4.476-4.476c11.828-11.772 19.06-17.921 23.556-19.936a5.6 5.6 0 0 1 3.098-.86zm3.965 2.96a2.9 2.9 0 0 1 2.043.844a2.8 2.8 0 0 1 .879 2.121a2.87 2.87 0 0 1-.985 2.07l-24.25 21.106l-2.617-2.617l22.887-22.68a2.9 2.9 0 0 1 2.043-.843zm2.994 6.698c-1.69 6.702-10.647 15.783-19.987 24.607l-3.777-3.773zM51.367 61.547l.274.27l3.513 3.513l-9.63 2.06zm5.793 5.84l.004.004l1.168 1.195l.018.084l.078.012l.248.254l.82.84l-5.385.66zm3.867 4.076l3.578 3.576A127 127 0 0 1 38.75 91.695a1.44 1.44 0 0 0-.777 1.653l1.035 4.5a.31.31 0 0 1 0 .363a.31.31 0 0 1-.414 0l-6.102-6.152L51.3 72.975zm-29.933 21.94l.869.814l4.492 4.492l-10.016-.648l4.655-4.659z"
                    />
                  </svg>
                </div>
                {/* User greet */}
                <Greeting
                  session={session!}
                  status={status}
                  theme={data?.theme}
                ></Greeting>
              </div>
              <div
                className={cn(
                  "mb-0 ml-2 mr-0 mt-0.5 flex h-5 flex-row items-center p-0 ring-2",
                  data?.theme === "moonlight"
                    ? " ring-sh-moonlight-highlight"
                    : data?.theme === "highcontrast"
                      ? "ring-sh-highcontrast-highlight"
                      : "ring-sh-shareholder2016-highlight",
                )}
              >
                <div>
                  <Search
                    form={form}
                    className="h-4 w-[140px] rounded-none"
                    icon={false}
                    theme={data?.theme as UITheme}
                    showSearch={showSearch}
                  />
                </div>
                {/* Submit button */}
                <Button
                  type="submit"
                  className="m-0 ml-0.5 h-4 p-0"
                  variant={"ghost"}
                  title="Search"
                  disabled={form.formState.isSubmitting}
                >
                  <Image
                    className="m-0 h-4 w-4 p-0"
                    src={searchIconBlue}
                    height={4}
                    width={4}
                    alt="submit"
                    unoptimized
                  />
                </Button>
              </div>
            </div>
          </div>
          {/* bottom */}
          <div className="ml-2 mt-1 flex w-full flex-row justify-between">
            {/* bottom-left */}

            <div className="hidden flex-row items-center space-x-3 lg:flex">
              <div className="mr-3 flex">
                {isLoading ? (
                  <Skeleton
                    className={cn(
                      "h-4 w-[130px] rounded-none",
                      data?.theme === "moonlight"
                        ? " bg-sh-moonlight-base"
                        : data?.theme === "highcontrast"
                          ? "bg-sh-highcontrast-base"
                          : "bg-sh-shareholder2016-base",
                    )}
                  />
                ) : (
                  <Select
                    onValueChange={(v) => {
                      if (v === "go-feedback") {
                        permanentRedirect("/feedback");
                      }

                      const option = data?.goToList?.options.find(
                        (o) => o.key === v,
                      );
                      const redirectUrl =
                        option?.url.replace("~", shareHolderUrl ?? "") ?? "/";
                      permanentRedirect(redirectUrl);
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-4 w-[130px] rounded-none pr-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base text-white"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base text-black"
                            : "bg-sh-shareholder2016-base text-black",
                      )}
                    >
                      <SelectValue placeholder="Go to..." />
                    </SelectTrigger>
                    <SelectContent
                      className={cn(
                        "mx-0 max-h-[300px] w-[130px] overflow-y-scroll rounded-none p-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectGroup className="m-0 p-0">
                        <SelectLabel
                          className={cn(
                            "m-0 p-0 text-left text-xsl",
                            data?.theme === "moonlight"
                              ? " text-white"
                              : data?.theme === "highcontrast"
                                ? "text-black"
                                : "text-black",
                          )}
                        >
                          Go to...
                        </SelectLabel>
                        {data?.goToList?.options
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((option) => (
                            <SelectItem
                              key={option.key}
                              value={option.key}
                              className={cn(
                                "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl focus:bg-blue-800 focus:text-white",
                                data?.theme === "moonlight"
                                  ? " text-white"
                                  : data?.theme === "highcontrast"
                                    ? "text-black"
                                    : "text-black",
                              )}
                            >
                              <p className="text-xsl">{option.description}</p>
                            </SelectItem>
                          ))}
                      </SelectGroup>
                      {/* AISearchAdmin gate dropped — feedback visible to all authenticated users. */}
                      {(
                        <SelectGroup className="m-0 p-0">
                          <SelectItem
                            key={"go-feedback"}
                            value={"go-feedback"}
                            className={cn(
                              "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl focus:bg-blue-800 focus:text-white",
                              data?.theme === "moonlight"
                                ? " text-white"
                                : data?.theme === "highcontrast"
                                  ? "text-black"
                                  : "text-black",
                            )}
                          >
                            <p className="text-xsl">{"AI Feedback"}</p>
                          </SelectItem>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="flex">
                {isLoading ? (
                  <Skeleton
                    className={cn(
                      "h-4 w-[130px] rounded-none",
                      data?.theme === "moonlight"
                        ? " bg-sh-moonlight-base"
                        : data?.theme === "highcontrast"
                          ? "bg-sh-highcontrast-base"
                          : "bg-sh-shareholder2016-base",
                    )}
                  />
                ) : (
                  <Select
                    onValueChange={(v) => {
                      const option = data?.addList?.options.find(
                        (o) => o.key === v,
                      );
                      const redirectUrl =
                        option?.url.replace("~", shareHolderUrl ?? "") ?? "/";
                      permanentRedirect(redirectUrl);
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-4 w-[130px] rounded-none pr-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base text-white"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectValue placeholder="Add new..." />
                    </SelectTrigger>
                    <SelectContent
                      className={cn(
                        "mx-0 max-h-[300px] w-[130px] overflow-y-scroll rounded-none p-0 text-xsl",
                        data?.theme === "moonlight"
                          ? " bg-sh-moonlight-base"
                          : data?.theme === "highcontrast"
                            ? "bg-sh-highcontrast-base"
                            : "bg-sh-shareholder2016-base",
                      )}
                    >
                      <SelectGroup className="m-0 p-0">
                        <SelectLabel
                          className={cn(
                            "m-0 p-0 text-left text-xsl",
                            data?.theme === "moonlight"
                              ? " text-white"
                              : data?.theme === "highcontrast"
                                ? "text-black"
                                : "text-black",
                          )}
                        >
                          Add new...
                        </SelectLabel>
                        {data?.addList?.options
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((option) => (
                            <SelectItem
                              key={option.key}
                              value={option.key}
                              className={cn(
                                "m-0 h-4 rounded-none p-0 px-1 text-left text-xsl text-black focus:bg-blue-800 focus:text-white",
                                data?.theme === "moonlight"
                                  ? " text-white"
                                  : data?.theme === "highcontrast"
                                    ? "text-black"
                                    : "text-black",
                              )}
                            >
                              <p className="text-xsl">{option.description}</p>
                            </SelectItem>
                          ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div
                className={cn(
                  "flex flex-row items-center ring-2",
                  data?.theme === "moonlight"
                    ? " ring-bg-sh-moonlight-highlight"
                    : data?.theme === "highcontrast"
                      ? "ring-bg-sh-highcontrast-highlight"
                      : "ring-bg-sh-shareholder2016-highlight",
                )}
              >
                <Input
                  className={cn(
                    "h-4 w-[180px] rounded-none placeholder:text-xsl",
                    data?.theme === "moonlight"
                      ? " bg-sh-moonlight-base text-white"
                      : data?.theme === "highcontrast"
                        ? "bg-sh-highcontrast-base text-black"
                        : "bg-sh-shareholder2016-base text-black",
                  )}
                  placeholder="Enter ticker or command"
                  type="text"
                  autoComplete="off"
                  value={text}
                  form="search-form"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    console.log("submitting");
                    e.preventDefault();
                    const redirectUrl = `${shareHolderUrl}/CompanyPageLoader?ticker=${text}`;
                    router.push(redirectUrl);
                  }}
                />
                <Button
                  variant={"ghost"}
                  className="my-0 ml-1 mr-0 h-4 rounded-none p-0"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log("clicking");
                    const redirectUrl = `${shareHolderUrl}/CompanyPageLoader?ticker=${text}`;
                    router.push(redirectUrl);
                  }}
                >
                  <Image
                    alt="search ticker in ShareHolder"
                    src={"/static/sh-icons/search-icon-blue.gif"}
                    width={4}
                    height={4}
                    className="m-0 h-4 w-4 p-0"
                    unoptimized
                  />
                </Button>
              </div>
            </div>
            {/* bottom-right */}
            <div className="hidden flex-row lg:flex">
              {isLoading ? (
                <Skeleton
                  className={cn(
                    "h-4 w-[230px] rounded-none",
                    data?.theme === "moonlight"
                      ? " bg-sh-moonlight-base"
                      : data?.theme === "highcontrast"
                        ? "bg-sh-highcontrast-base"
                        : "bg-sh-shareholder2016-base",
                  )}
                />
              ) : (
                <div className="flex flex-row space-x-1">
                  {data?.toolbar?.options
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((option) => (
                      <a
                        key={option.key}
                        href={
                          option.newWindow
                            ? ""
                            : option?.url.replace("~", shareHolderUrl ?? "") ??
                              "/"
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          if (!option.newWindow) {
                            router.push(
                              option?.url.replace("~", shareHolderUrl ?? "") ??
                                "/",
                            );
                          } else {
                            window.open(
                              option?.url.replace("~", shareHolderUrl ?? "") ??
                                "/",
                              "newwindow",
                              `width=${option.windowWidth},height=${option.windowHeight}`,
                            );
                          }
                        }}
                      >
                        <Image
                          key={option.key}
                          src={`/static/sh-icons/${option.icon.toLowerCase()}`}
                          alt={option.description}
                          width={10}
                          height={10}
                          className="h-4 w-4"
                          unoptimized
                        />
                      </a>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
}

function Greeting(props: {
  status: "loading" | "authenticated" | "unauthenticated";
  session: Session;
  theme?: UITheme;
}) {
  return (
    <>
      <span className="items-end">
        {props?.status === "loading" ? (
          <div className="flex justify-end">
            <Skeleton
              className={cn(
                "h-4 w-[250px] rounded-none",
                props.theme === "moonlight"
                  ? " bg-sh-moonlight-base"
                  : props.theme === "highcontrast"
                    ? "bg-sh-highcontrast-base"
                    : "bg-sh-shareholder2016-base",
              )}
            />
          </div>
        ) : (
          <>
            <p className="text-right text-xsm font-medium">
              Logged in as <b>{props.session?.user?.name}</b> to{" "}
              <b className="underline">LFG</b>
            </p>
          </>
        )}
      </span>
    </>
  );
}
